import axios from "axios";
import { publicApi, setPublicBaseURL } from "./axios.public";
import { normalizeAxiosError } from "./axios.error";
import { usePortalAuthStore } from "../../core/stores/auth/portal-auth.store";
import { PortalAuthHandler } from "./portal-auth.handler";
let axiosInstance = null;
const getBaseUrl = () => {
    const envUrl = import.meta.env?.VITE_API_URL || process.env.VITE_API_URL;
    if (envUrl) {
        return envUrl;
    }
    // Default development backend port
    return "http://localhost:8080/api";
};
export const portalPrivateApi = (baseURL) => {
    if (baseURL)
        setPublicBaseURL(baseURL);
    if (!axiosInstance) {
        axiosInstance = axios.create({
            baseURL: baseURL || getBaseUrl(),
            withCredentials: true,
            timeout: 120000, // 120 detik — selaras dengan gateway response-timeout
        });
        // ---------------------------
        // request
        // ---------------------------
        axiosInstance.interceptors.request.use((config) => {
            const state = usePortalAuthStore.getState();
            const token = state.accessToken;
            const lembaga = state.lembaga?.idLembaga;
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
                if (lembaga) {
                    config.headers['X-Id-Lembaga'] = lembaga;
                }
            }
            return config;
        });
        // ---------------------------
        // response
        // ---------------------------
        let isRefreshing = false;
        let failedQueue = [];
        const processQueue = (error, token = null) => {
            failedQueue.forEach((prom) => {
                if (error) {
                    prom.reject(error);
                }
                else {
                    prom.resolve(token);
                }
            });
            failedQueue = [];
        };
        axiosInstance.interceptors.response.use((res) => res, async (err) => {
            const originalRequest = err.config;
            const status = err.response?.status;
            // 1. Auto Refresh Logic (Hanya untuk 401 dan bukan retry)
            if (status === 401 && !originalRequest?._retry) {
                const code = PortalAuthHandler.getSecurityCode(err);
                // Jika kode menandakan kegagalan sesi permanen (Hijack/Mismatch), jangan refresh
                if (code === "ERR_DEVICE_MISMATCH" || code === "ERR_INVALID_JTI") {
                    PortalAuthHandler.handleGlobalError(status, err);
                    return Promise.reject(err);
                }
                // Jika sedang refresh, masukkan ke antrean
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return axiosInstance(originalRequest);
                    })
                        .catch((err) => {
                        return Promise.reject(err);
                    });
                }
                originalRequest._retry = true;
                isRefreshing = true;
                const refreshToken = usePortalAuthStore.getState().refreshToken;
                try {
                    const payload = refreshToken ? { refreshToken } : {};
                    const { data } = await publicApi.post("/auth/portal/refresh", payload);
                    const refreshData = data.data;
                    const state = usePortalAuthStore.getState();
                    if (state.user) {
                        state.setSession({
                            user: state.user,
                            lembaga: state.lembaga || undefined,
                            accessToken: refreshData.accessToken,
                            accessExpiredAt: refreshData.accessExpiredAt,
                            refreshToken: state.refreshToken || undefined,
                            refreshExpiredAt: state.refreshExpiredAt || undefined,
                        });
                    }
                    processQueue(null, refreshData.accessToken);
                    isRefreshing = false;
                    originalRequest.headers.Authorization = `Bearer ${refreshData.accessToken}`;
                    return axiosInstance(originalRequest);
                }
                catch (e) {
                    processQueue(e, null);
                    isRefreshing = false;
                    // Only clear session when the refresh token itself is invalid/expired (HTTP 401).
                    // Other error codes (404 endpoint-not-found, 429 rate-limit, 5xx server error)
                    // must NOT clear the session — they are transient failures, not auth failures.
                    const refreshStatus = e?.response?.status;
                    if (refreshStatus === 401) {
                        PortalAuthHandler.handleGlobalError(401, e);
                    }
                    // For any other error (404, 5xx, network), reject silently so the
                    // original request also rejects without destroying the auth session.
                    return Promise.reject(e);
                }
            }
            // 2. Global Error Handling (401/403/429)
            if (status === 401 || status === 403 || status === 429) {
                // Jika di halaman login, kita mungkin ingin menangani error secara lokal
                const isLoginPage = window.location.pathname.includes("/login");
                PortalAuthHandler.handleGlobalError(status, err, { skipRedirect: isLoginPage });
            }
            return Promise.reject(normalizeAxiosError(err));
        });
    }
    return axiosInstance;
};
